import { atom, selector } from "recoil";
import { evidenceAtom, userProgramsAtom } from "./State";
import { EvidenceCustomTag, EvidenceStandardTag, IEvidence, ProgramData } from "@interfaces/Interfaces";
import { orderBy, sortBy, uniqBy } from "lodash";
import { getFilteredEvidence } from "@utils/evidenceUtils";

export type EvidenceSearchFilter = {
  type: string;
  label: string;
  option: string;
};

export const getGeneralEvidenceFilters: EvidenceSearchFilter[] = [
  { type: "General", label: "Personal", option: "Personal" },
  { type: "General", label: "Tagged for audit", option: "Tagged for audit" },
];

export const getDraftsFilters: EvidenceSearchFilter[] = [
  { type: "Drafts", label: "Include", option: "Include Drafts" },
  { type: "Drafts", label: "Exclude", option: "Exclude Drafts" },
];

export const getEvidenceDateFilters: EvidenceSearchFilter[] = [
  { type: "Date Range", label: "3 months", option: "3 months" },
  { type: "Date Range", label: "6 months", option: "6 months" },
  { type: "Date Range", label: "1 year", option: "1 year" },
  { type: "Date Range", label: "2 years", option: "2 years" },
];

export const evidenceFiltersAtom = atom<EvidenceSearchFilter[]>({
  key: "evidenceFiltersAtom",
  default: [],
  effects: [
    ({ onSet, setSelf }) => {
      setSelf([]);

      onSet((newValue, _oldValue, isReset) => {
        if (isReset) {
          return [];
        } else if (newValue !== undefined) {
          return newValue;
        }
      });
    },
  ],
});

export const CPDActivityFilterOptionsSelector = selector<EvidenceSearchFilter[]>({
  key: "CPDActivityFilterOptionsSelector",
  get: ({ get }) => {
    const evidence: IEvidence[] = get(evidenceAtom);

    if (evidence) {
      const allActivities = evidence
        .filter((item) => item.activity)
        .map((item) => ({ type: "CPD Activity", label: item.activity!.type, option: item.activity!.type }));

      return uniqBy(
        sortBy(allActivities, (item) => item.label),
        (item) => item.option
      );
    }

    return [];
  },
});

export const programModulesFilterOptionsSelector = selector<EvidenceSearchFilter[]>({
  key: "programModulesFilterOptionsSelector",
  get: ({ get }) => {
    const evidence: IEvidence[] = get(evidenceAtom);
    const programs: ProgramData[] = get(userProgramsAtom);

    if (evidence) {
      const allPrograms = evidence
        .filter((item) => item.evidenceJSON)
        .map((item) => {
          if (typeof item.evidenceJSON === "string") {
            return JSON.parse(item.evidenceJSON).programInfo.programID;
          }

          return item.evidenceJSON!.programInfo.programID;
        })
        .map((id) => programs.find((item) => item.ID === id))
        .filter((item) => item !== undefined && item.Title !== undefined)
        .map((item) => ({ type: "Program Modules", label: item!.Title!, option: item!.ID }));

      return uniqBy(
        sortBy(allPrograms, (item) => item.label),
        (item) => item.option
      );
    }

    return [];
  },
});

export const NOSTagsFilterOptionsSelector = selector<EvidenceSearchFilter[]>({
  key: "NOSTagsFilterOptionsSelector",
  get: ({ get }) => {
    const evidence: IEvidence[] = get(evidenceAtom);

    if (evidence) {
      const allNOSTags = evidence
        .filter((item) => item.standardTags)
        .map((item) =>
          item.standardTags!.map((tag: EvidenceStandardTag) => ({ type: "NOS", label: tag.tag, option: tag.tag }))
        )
        .flat();

      return uniqBy(
        sortBy(allNOSTags, (item) => item.label),
        (item) => item.option
      );
    }

    return [];
  },
});

export const customTagsFilterOptionsSelector = selector<EvidenceSearchFilter[]>({
  key: "customTagsFilterOptionsSelector",
  get: ({ get }) => {
    const evidence: IEvidence[] = get(evidenceAtom);

    if (evidence) {
      const allCustomTags = evidence
        .filter((item) => item.customTags)
        .map((item) =>
          item.customTags!.map((tag: EvidenceCustomTag) => ({ type: "Custom Tags", label: tag.tag, option: tag.tag }))
        )
        .flat();

      return uniqBy(
        sortBy(allCustomTags, (item) => item.label),
        (item) => item.option
      );
    }

    return [];
  },
});

export const filteredEvidenceSelector = selector<IEvidence[]>({
  key: "filteredEvidenceSelector",
  get: ({ get }) => {
    const evidence: IEvidence[] = get(evidenceAtom);
    const filters: EvidenceSearchFilter[] = get(evidenceFiltersAtom);

    if (!evidence || evidence.length === 0) return [];

    let sortedEvidence = structuredClone(evidence);
    sortedEvidence = orderBy(sortedEvidence, ["date"], ["desc"]);

    if (filters.length === 0) return sortedEvidence;

    const filtered = getFilteredEvidence(filters, sortedEvidence);

    return filtered;
  },
});
